<script setup lang="ts">
// The live K_t card + price-coverage bar + per-holding table of a live-synth fund
// (generalised 2026-10-15 from CharterisSignalTab; used by CharterisSignalTab and
// AmatiSignalTab). Renders nothing until the fund's /synth-nav-live answers
// (switch off or not viewable = 404 = null). The tab owns the poll
// (useFundLiveSynthNav) and passes the payload in.
import { computed } from 'vue'
import type { LiveSynthNav } from '../../api/client'
import { mapLiveKtHoldings, coverageSegments } from '../../lib/liveKtHoldingsRows'

const props = withDefaults(defineProps<{
  live?: LiveSynthNav | null
  /** data-testid prefix: `{prefix}-live-kt` and `{prefix}-kt-holdings`. */
  testIdPrefix: string
  /** Tooltip per tier (live / degraded / critical / closed): the fund's own metal feeds. */
  tierTitles: Record<string, string>
  /** Header of the per-fund tag column (Charteris: m, Amati: the roll future). */
  tagLabel?: string
  /** Optional small note under the card (e.g. Amati's uncalibrated betas). */
  note?: string | null
}>(), { live: null, tagLabel: 'm', note: null })

// 'closed' (2026-10-10): COMEX metals outside Globex hours — the last-close estimate is kept and the badge is neutral,
// never red. Only 'critical' (a feed fault while the market trades) means the return has been zeroed.
const KT_TIER: Record<string, string> = { live: 'kt-badge-green', degraded: 'kt-badge-amber', critical: 'kt-badge-red', closed: 'kt-badge-neutral' }
const tierClass = computed(() => KT_TIER[props.live?.tier ?? ''] ?? 'kt-badge-neutral')
const tierTitle = computed(() => props.tierTitles[props.live?.tier ?? ''] ?? '')
const realPct = computed(() => {
  const c = props.live?.coverage
  return c ? Math.round(c.live_pct + c.delayed_pct + (c.stale_pct ?? 0)) : null
})
const holdings = computed(() => (props.live ? mapLiveKtHoldings(props.live.holdings) : []))
const coverage = computed(() => (props.live ? coverageSegments(props.live.coverage) : []))
const fmtPct = (p: number) => `${p >= 0 ? '+' : ''}${p.toFixed(2)}%`
</script>

<template>
  <div v-if="live" class="card live-kt" :data-testid="`${testIdPrefix}-live-kt`">
    <div class="clk-head">
      <span class="clk-title">Live K_t</span>
      <span class="tier-badge" :class="tierClass" :title="tierTitle">{{ (live.tier || '').toUpperCase() }}</span>
    </div>
    <div class="clk-row">
      <span class="clk-nav">{{ live.synth_nav_pence.toFixed(2) }}<span class="clk-unit">p</span></span>
      <span class="clk-ret" :class="live.return_pct >= 0 ? 'positive' : 'negative'">{{ (live.return_pct >= 0 ? '+' : '') + live.return_pct.toFixed(2) }}%</span>
    </div>
    <div v-if="realPct != null" class="clk-cover">{{ realPct }}% priced from live, delayed or last-close quotes</div>
    <div v-if="note" class="clk-note" :data-testid="`${testIdPrefix}-live-kt-note`">{{ note }}</div>
  </div>

  <!-- Live/delayed K_t portfolio: price-coverage bar + per-holding breakdown, sorted by contribution. -->
  <div v-if="live" class="card live-kt-holdings" :data-testid="`${testIdPrefix}-kt-holdings`">
    <section class="ckt-cover">
      <div class="ckt-cover-bar" role="img" aria-label="Price coverage">
        <span v-for="s in coverage" :key="s.key" class="ckt-cover-seg" :class="`seg-${s.tone}`" :style="{ width: s.pct + '%' }" />
      </div>
      <ul class="ckt-cover-legend">
        <li v-for="s in coverage" :key="s.key"><i :class="`dot-${s.tone}`" />{{ s.label }} {{ s.pct.toFixed(1) }}%</li>
      </ul>
    </section>
    <div class="ckt-block-head"><h3>Holdings</h3><span class="ckt-count">{{ holdings.length }}</span></div>
    <table class="ckt-table">
      <thead>
        <tr><th class="l">Holding</th><th>Weight</th><th class="src">Source</th><th>Move</th><th>Contribution</th><th class="num">β</th><th class="num">{{ tagLabel }}</th></tr>
      </thead>
      <tbody>
        <tr v-for="r in holdings" :key="r.name">
          <td class="l">{{ r.name }}</td>
          <td>{{ r.weightPct.toFixed(2) }}%</td>
          <td class="src"><span class="hbadge" :class="`seg-${r.badge.tone}`">{{ r.badge.label }}</span></td>
          <td :class="r.returnPct >= 0 ? 'kt-up' : 'kt-down'">{{ fmtPct(r.returnPct) }}</td>
          <td class="contrib" :class="r.contributionBp >= 0 ? 'kt-up' : 'kt-down'">{{ (r.contributionBp >= 0 ? '+' : '') + r.contributionBp.toFixed(1) }} bp</td>
          <td class="num">{{ r.betaText }}</td>
          <td class="num">{{ r.mText }}</td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<style scoped>
.live-kt { display: flex; flex-direction: column; gap: 4px; }
.clk-head { display: flex; align-items: center; justify-content: space-between; }
.clk-title { font-size: 0.82rem; color: #8b949e; }
.clk-row { display: flex; align-items: baseline; gap: 10px; }
.clk-nav { font-size: 1.5rem; font-weight: 600; font-variant-numeric: tabular-nums; color: #e6e6ef; }
.clk-unit { font-size: 0.8rem; color: #8b949e; margin-left: 1px; }
.clk-ret { font-size: 0.95rem; font-weight: 600; font-variant-numeric: tabular-nums; }
.clk-ret.positive { color: #3fb950; } .clk-ret.negative { color: #f85149; }
.clk-cover { font-size: 0.74rem; color: #8b949e; }
.clk-note { font-size: 0.72rem; color: #8b949e; font-style: italic; }
.tier-badge { font-size: 0.66rem; font-weight: 700; letter-spacing: 0.04em; padding: 2px 6px; border-radius: 4px; }
.kt-badge-green { background: rgba(63,185,80,0.15); color: #3fb950; }
.kt-badge-amber { background: rgba(210,153,34,0.15); color: #d29922; }
.kt-badge-red { background: rgba(248,81,73,0.15); color: #f85149; }
.kt-badge-neutral { background: rgba(154,154,168,0.15); color: #9a9aa8; }

.live-kt-holdings { display: flex; flex-direction: column; gap: 0.7rem; }
.kt-up { color: var(--green, #3fb950); }
.kt-down { color: var(--red, #f85149); }

.ckt-cover-bar { display: flex; height: 8px; border-radius: 4px; overflow: hidden; background: rgba(255,255,255,0.04); }
.ckt-cover-seg { height: 100%; }
.ckt-cover-legend { list-style: none; display: flex; flex-wrap: wrap; gap: 0.1rem 1rem; margin: 0.45rem 0 0; padding: 0; font-size: 0.76rem; color: var(--muted, #9a9aa8); }
.ckt-cover-legend li { display: flex; align-items: center; gap: 0.35rem; }
.ckt-cover-legend i { width: 8px; height: 8px; border-radius: 2px; display: inline-block; }

.seg-live, .dot-live { background: var(--green, #3fb950); }
.seg-delayed, .dot-delayed { background: #58a6ff; }
.seg-stale, .dot-stale { background: #7c8db5; }
.seg-proxy, .dot-proxy { background: var(--muted, #9a9aa8); }
.seg-warn, .dot-warn { background: var(--amber, #d29922); }
.seg-muted, .dot-muted { background: #6e7681; }
.seg-flat, .dot-flat { background: #3a3f4b; }

.ckt-block-head { display: flex; align-items: center; justify-content: space-between; margin-bottom: 0.1rem; }
.live-kt-holdings h3 { font-size: 0.9rem; font-weight: 600; margin: 0; color: var(--text, #e6e6ef); }
.ckt-count { font-size: 0.78rem; color: var(--muted, #9a9aa8); }

.ckt-table { width: 100%; border-collapse: collapse; font-size: 0.82rem; font-variant-numeric: tabular-nums; }
.ckt-table th { text-align: right; font-weight: 500; color: var(--muted, #9a9aa8); padding: 0.3rem 0.5rem; border-bottom: 1px solid var(--border, #2a2a2a); font-size: 0.74rem; }
.ckt-table td { text-align: right; padding: 0.3rem 0.5rem; border-bottom: 1px solid rgba(255,255,255,0.04); color: var(--text, #e6e6ef); }
.ckt-table .l { text-align: left; } .ckt-table .num { color: var(--muted, #9a9aa8); }
.hbadge { font-size: 0.66rem; font-weight: 600; padding: 0.08rem 0.35rem; border-radius: 3px; color: #0b0b12; }

/* Phone: drop the detail columns, keep name · source · contribution. */
@media (max-width: 640px) {
  .ckt-table th:nth-child(2), .ckt-table td:nth-child(2),
  .ckt-table th:nth-child(4), .ckt-table td:nth-child(4),
  .ckt-table .num { display: none; }
}
</style>
